import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { Button } from './Button';
import { COLORS } from '../config/Colors';
import type { BetDto } from '../types/api';

export class HistoryPopup extends Container {
  private background: Graphics;
  private contentContainer: Container;
  private closeButton!: Button;
  private titleText!: Text;
  private betTexts: Text[] = [];
  private rowBackgrounds: Graphics[] = [];
  
  private currentPage: number = 1;
  private betsPerPage: number = 8;
  private bets: BetDto[] = [];
  private onNextPage?: () => void;
  private onPrevPage?: () => void;
  
  constructor() {
    super();
    
    this.background = new Graphics();
    this.contentContainer = new Container();
    
    this.drawBackground();
    this.createTitle();
    this.createCloseButton();
    this.createNavigationButtons();
    
    this.visible = false;
    this.zIndex = 200;
  }
  
  private drawBackground(): void {
    const width = 720;
    const height = 700;
    
    // Semi-transparent overlay
    this.background.rect(-384, -512, 768, 1024);
    this.background.fill({ color: 0x000000, alpha: 0.7 });
    
    // Main popup background
    this.background.roundRect(-width/2, -height/2, width, height, 20);
    this.background.fill({ color: 0xFFF8DC }); // Cornsilk (light parchment)
    this.background.stroke({ color: 0xDAA520, width: 4 }); // Goldenrod border
    
    this.addChild(this.background);
    this.addChild(this.contentContainer);
  }
  
  private createTitle(): void {
    const titleStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 28,
      fontWeight: 'bold',
      fill: COLORS.PRIMARY_DARK,
      stroke: { color: 0xFFFFFF, width: 2 }
    });
    
    this.titleText = new Text({ text: '📜 Bet History', style: titleStyle });
    this.titleText.anchor.set(0.5);
    this.titleText.x = 0;
    this.titleText.y = -300;
    this.contentContainer.addChild(this.titleText);
  }
  
  private createCloseButton(): void {
    this.closeButton = new Button('✕', 40, 40, {
      backgroundColor: 0xC62828,
      hoverColor: 0xE53935,
      fontSize: 24
    });
    this.closeButton.x = 320;
    this.closeButton.y = -300;
    this.closeButton.setCallback(() => this.close());
    this.contentContainer.addChild(this.closeButton);
  }
  
  private createNavigationButtons(): void {
    const prevButton = new Button('◀ Previous', 100, 40, {
      backgroundColor: COLORS.SECONDARY_DARK,
      hoverColor: COLORS.SECONDARY,
      fontSize: 16
    });
    prevButton.x = -250;
    prevButton.y = 280;
    prevButton.setCallback(() => {
      if (this.onPrevPage) this.onPrevPage();
    });
    this.contentContainer.addChild(prevButton);
    
    const nextButton = new Button('Next ▶', 100, 40, {
      backgroundColor: COLORS.SECONDARY_DARK,
      hoverColor: COLORS.SECONDARY,
      fontSize: 16
    });
    nextButton.x = 250;
    nextButton.y = 280;
    nextButton.setCallback(() => {
      if (this.onNextPage) this.onNextPage();
    });
    this.contentContainer.addChild(nextButton);
  }
  
  public setBets(bets: BetDto[]): void {
    console.log('HistoryPopup.setBets called with', bets.length, 'bets');
    this.bets = bets;
    this.currentPage = 1; // Reset to first page when new bets are set
    this.updateDisplay();
  }
  
  public updateDisplay(): void {
    console.log('HistoryPopup.updateDisplay called');
    
    // Clear previous bet texts and row backgrounds
    this.betTexts.forEach(text => this.contentContainer.removeChild(text));
    this.betTexts = [];
    this.rowBackgrounds.forEach(bg => this.contentContainer.removeChild(bg));
    this.rowBackgrounds = [];
    
    const startIdx = (this.currentPage - 1) * this.betsPerPage;
    const pageBets = this.bets.slice(startIdx, startIdx + this.betsPerPage);
    
    console.log('Displaying bets', startIdx, 'to', startIdx + pageBets.length - 1);
    
    const headerStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 18,
      fontWeight: 'bold',
      fill: 0xFFF8DC // Light parchment
    });
    
    const betStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 15,
      fill: COLORS.TEXT_PRIMARY
    });
    
    const winStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 15,
      fontWeight: 'bold',
      fill: 0x00AA00 // Darker green
    });
    
    const lossStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 15,
      fontWeight: 'bold',
      fill: 0xCC0000 // Darker red
    });
    
    let yOffset = -240;
    
    // Header background
    const headerBg = new Graphics();
    headerBg.roundRect(-340, yOffset - 10, 680, 35, 8);
    headerBg.fill({ color: 0x8B4513 }); // Saddle brown
    this.contentContainer.addChild(headerBg);
    this.rowBackgrounds.push(headerBg);
    
    // Header texts with fixed positions
    const headerTime = new Text({ text: 'Time', style: headerStyle });
    headerTime.x = -320;
    headerTime.y = yOffset;
    this.contentContainer.addChild(headerTime);
    this.betTexts.push(headerTime);
    
    const headerBet = new Text({ text: 'Bet', style: headerStyle });
    headerBet.x = -150;
    headerBet.y = yOffset;
    this.contentContainer.addChild(headerBet);
    this.betTexts.push(headerBet);
    
    const headerResult = new Text({ text: 'Result', style: headerStyle });
    headerResult.x = 30;
    headerResult.y = yOffset;
    this.contentContainer.addChild(headerResult);
    this.betTexts.push(headerResult);
    
    const headerAmount = new Text({ text: 'Amount', style: headerStyle });
    headerAmount.x = 180;
    headerAmount.y = yOffset;
    this.contentContainer.addChild(headerAmount);
    this.betTexts.push(headerAmount);
    
    yOffset += 40;
    
    if (pageBets.length === 0) {
      const noBets = new Text({ 
        text: 'No bets yet',
        style: new TextStyle({
          fontFamily: 'Spenbeb, Georgia, serif',
          fontSize: 20,
          fill: COLORS.TEXT_SECONDARY
        })
      });
      noBets.anchor.set(0.5);
      noBets.x = 0;
      noBets.y = 0;
      this.contentContainer.addChild(noBets);
      this.betTexts.push(noBets);
      console.log('No bets to display');
      return;
    }
    
    console.log('Displaying', pageBets.length, 'bets on page', this.currentPage);
    
    pageBets.forEach((bet, idx) => {
      const time = new Date(bet.createdAt).toLocaleTimeString();
      const stake = bet.grossStake;
      const result = bet.status === 'WON' ? 'WIN' : 'LOSS';
      const amount = bet.netWinning;
      
      const isWin = bet.netWinning > 0;
      
      // Alternating row background
      const rowBg = new Graphics();
      const bgColor = idx % 2 === 0 ? 0xF5E6D3 : 0xEDE0C8; // Alternating light brown/tan
      rowBg.roundRect(-340, yOffset - 8, 680, 30, 4);
      rowBg.fill({ color: bgColor });
      this.contentContainer.addChild(rowBg);
      this.rowBackgrounds.push(rowBg);
      
      console.log(`Bet ${idx}:`, time, stake, result, amount);
      
      // Time column
      const timeText = new Text({ text: time, style: betStyle });
      timeText.x = -320;
      timeText.y = yOffset;
      this.contentContainer.addChild(timeText);
      this.betTexts.push(timeText);
      
      // Bet column
      const betText = new Text({ text: String(stake), style: betStyle });
      betText.x = -150;
      betText.y = yOffset;
      this.contentContainer.addChild(betText);
      this.betTexts.push(betText);
      
      // Result column
      const resultText = new Text({ text: result, style: isWin ? winStyle : lossStyle });
      resultText.x = 30;
      resultText.y = yOffset;
      this.contentContainer.addChild(resultText);
      this.betTexts.push(resultText);
      
      // Amount column
      const amountText = new Text({ 
        text: `${isWin ? '+' : ''}${amount}`, 
        style: isWin ? winStyle : lossStyle 
      });
      amountText.x = 180;
      amountText.y = yOffset;
      this.contentContainer.addChild(amountText);
      this.betTexts.push(amountText);
      
      yOffset += 32;
    });
    
    // Page indicator
    const totalPages = Math.ceil(this.bets.length / this.betsPerPage);
    const pageText = new Text({ 
      text: `Page ${this.currentPage} of ${totalPages || 1}`,
      style: new TextStyle({
        fontFamily: 'Spenbeb, Georgia, serif',
        fontSize: 16,
        fill: COLORS.TEXT_SECONDARY
      })
    });
    pageText.anchor.set(0.5);
    pageText.x = 0;
    pageText.y = 260;
    this.contentContainer.addChild(pageText);
    this.betTexts.push(pageText);
  }
  
  public open(): void {
    console.log('HistoryPopup.open called, current visible:', this.visible);
    this.visible = true;
    console.log('HistoryPopup visible set to:', this.visible);
  }
  
  public close(): void {
    console.log('HistoryPopup.close called, current visible:', this.visible);
    this.visible = false;
    console.log('HistoryPopup visible set to:', this.visible);
  }
  
  public toggle(): void {
    console.log('HistoryPopup.toggle called, current visible:', this.visible);
    this.visible = !this.visible;
    console.log('HistoryPopup visible toggled to:', this.visible);
    if (this.visible) {
      console.log('Updating display with', this.bets.length, 'bets');
      this.updateDisplay();
    }
  }
  
  public onNext(callback: () => void): void {
    this.onNextPage = callback;
  }
  
  public onPrevious(callback: () => void): void {
    this.onPrevPage = callback;
  }
}